import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from './app/ThemeProvider';
import { AuthProvider } from './app/auth/AuthProvider';
import { AuthGate } from './app/auth/AuthGate';
import { AdminOnlyGate } from './app/auth/AdminOnlyGate';
import { CatalogueProvider } from './app/CatalogueProvider';
import { AppShell } from './ui/navigation/AppShell';
import { RouteLoading } from './ui/navigation/RouteLoading';
import { ROUTES } from './ui/navigation/routes';
import { DashboardScreen } from './features/dashboard/DashboardScreen';
import { ActiveWorkoutScreen } from './features/workout/ActiveWorkoutScreen';
import { WorkoutSummaryScreen } from './features/workout/WorkoutSummary';

/**
 * Everything except Dashboard and the two active-workout screens is
 * code-split (`PHASE-0-ARCHITECTURE.md` §18: route-level splitting, workout
 * prioritised). Dashboard is the default landing screen — splitting it would
 * add a loading flash to the very first paint most sessions ever see.
 * `ActiveWorkoutScreen`/`WorkoutSummaryScreen` stay eager because "the
 * workout route is prioritised" reads as "never behind a lazy boundary,"
 * not just "loaded first among lazy ones" — no chunk fetch should ever sit
 * between finishing a set and seeing the next one. Phase 10.
 */
const WorkoutScreen = lazy(() => import('./features/workout/WorkoutScreen').then((m) => ({ default: m.WorkoutScreen })));
const PlansScreen = lazy(() => import('./features/plans/PlansScreen').then((m) => ({ default: m.PlansScreen })));
const PlanDetailScreen = lazy(() =>
  import('./features/plans/PlanDetailScreen').then((m) => ({ default: m.PlanDetailScreen })),
);
const PlanBuilderScreen = lazy(() =>
  import('./features/plans/PlanBuilderScreen').then((m) => ({ default: m.PlanBuilderScreen })),
);
const ExercisesScreen = lazy(() =>
  import('./features/exercises/ExercisesScreen').then((m) => ({ default: m.ExercisesScreen })),
);
const ExerciseDetailScreen = lazy(() =>
  import('./features/exercises/ExerciseDetailScreen').then((m) => ({ default: m.ExerciseDetailScreen })),
);
const ExerciseFormScreen = lazy(() =>
  import('./features/exercises/ExerciseFormScreen').then((m) => ({ default: m.ExerciseFormScreen })),
);
const HistoryScreen = lazy(() => import('./features/history/HistoryScreen').then((m) => ({ default: m.HistoryScreen })));
const HistoryDetailScreen = lazy(() =>
  import('./features/history/HistoryDetailScreen').then((m) => ({ default: m.HistoryDetailScreen })),
);
const ProgressScreen = lazy(() =>
  import('./features/progress/ProgressScreen').then((m) => ({ default: m.ProgressScreen })),
);
const PersonalRecordsScreen = lazy(() =>
  import('./features/progress/PersonalRecordsScreen').then((m) => ({ default: m.PersonalRecordsScreen })),
);
const PersonalRecordDetailScreen = lazy(() =>
  import('./features/progress/PersonalRecordDetailScreen').then((m) => ({ default: m.PersonalRecordDetailScreen })),
);
const ExerciseTrendsScreen = lazy(() =>
  import('./features/progress/ExerciseTrendsScreen').then((m) => ({ default: m.ExerciseTrendsScreen })),
);
const ExerciseTrendDetailScreen = lazy(() =>
  import('./features/progress/ExerciseTrendDetailScreen').then((m) => ({ default: m.ExerciseTrendDetailScreen })),
);
const MeasurementsScreen = lazy(() =>
  import('./features/progress/MeasurementsScreen').then((m) => ({ default: m.MeasurementsScreen })),
);
const MeasurementDetailScreen = lazy(() =>
  import('./features/progress/MeasurementDetailScreen').then((m) => ({ default: m.MeasurementDetailScreen })),
);
const SettingsScreen = lazy(() =>
  import('./features/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
);
const AdminDashboardScreen = lazy(() =>
  import('./features/admin/AdminDashboardScreen').then((m) => ({ default: m.AdminDashboardScreen })),
);
const AdminUsersScreen = lazy(() =>
  import('./features/admin/AdminUsersScreen').then((m) => ({ default: m.AdminUsersScreen })),
);
const AdminUserDetailScreen = lazy(() =>
  import('./features/admin/AdminUserDetailScreen').then((m) => ({ default: m.AdminUserDetailScreen })),
);
const AdminAccountScreen = lazy(() =>
  import('./features/admin/AdminAccountScreen').then((m) => ({ default: m.AdminAccountScreen })),
);
const AdminUsageScreen = lazy(() =>
  import('./features/admin/AdminUsageScreen').then((m) => ({ default: m.AdminUsageScreen })),
);

/**
 * Route table.
 *
 * Exported separately from `App` so tests can mount it inside a
 * `MemoryRouter` and drive navigation without a browser history.
 */
export function AppRoutes(): React.JSX.Element {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route path={ROUTES.dashboard} element={<DashboardScreen />} />
          <Route path={ROUTES.workout} element={<WorkoutScreen />} />
          <Route path={ROUTES.workoutSummary} element={<WorkoutSummaryScreen />} />
          <Route path={ROUTES.workoutSession} element={<ActiveWorkoutScreen />} />
          <Route path={ROUTES.plans} element={<PlansScreen />} />
          <Route path={ROUTES.planNew} element={<PlanBuilderScreen />} />
          <Route path={ROUTES.planEdit} element={<PlanBuilderScreen />} />
          <Route path={ROUTES.planDetail} element={<PlanDetailScreen />} />
          <Route path={ROUTES.exercises} element={<ExercisesScreen />} />
          <Route path={ROUTES.exerciseNew} element={<ExerciseFormScreen />} />
          <Route path={ROUTES.exerciseEdit} element={<ExerciseFormScreen />} />
          <Route path={ROUTES.exerciseDetail} element={<ExerciseDetailScreen />} />
          <Route path={ROUTES.history} element={<HistoryScreen />} />
          <Route path={ROUTES.historyDetail} element={<HistoryDetailScreen />} />
          <Route path={ROUTES.progress} element={<ProgressScreen />} />
          <Route path={ROUTES.progressRecords} element={<PersonalRecordsScreen />} />
          <Route path={ROUTES.progressRecordDetail} element={<PersonalRecordDetailScreen />} />
          <Route path={ROUTES.progressTrends} element={<ExerciseTrendsScreen />} />
          <Route path={ROUTES.progressTrendDetail} element={<ExerciseTrendDetailScreen />} />
          <Route path={ROUTES.progressMeasurements} element={<MeasurementsScreen />} />
          <Route path={ROUTES.progressMeasurementDetail} element={<MeasurementDetailScreen />} />
          <Route path={ROUTES.settings} element={<SettingsScreen />} />

          {/* Administration (Master Phase 11) — a UX convenience gate only;
              every /api/admin/* endpoint independently re-checks
              role==='admin' server-side regardless of this. */}
          <Route element={<AdminOnlyGate />}>
            <Route path={ROUTES.adminDashboard} element={<AdminDashboardScreen />} />
            <Route path={ROUTES.adminUsers} element={<AdminUsersScreen />} />
            <Route path={ROUTES.adminUserDetail} element={<AdminUserDetailScreen />} />
            <Route path={ROUTES.adminAccount} element={<AdminAccountScreen />} />
            <Route path={ROUTES.adminUsage} element={<AdminUsageScreen />} />
          </Route>

          <Route path="*" element={<Navigate to={ROUTES.dashboard} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export function App(): React.JSX.Element {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AuthGate>
            {/* Inside the gate: there is no reason to seed a catalogue for
                someone who has not signed in, and seeding must not delay the
                login screen. It does not block rendering — see
                CatalogueProvider's header. */}
            <CatalogueProvider>
              <AppRoutes />
            </CatalogueProvider>
          </AuthGate>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
